'use client'

import { useEffect } from 'react'
import { useSearchParams } from 'next/navigation'
import { captureEvent } from '@/lib/analytics'

interface ArticleTrackerProps {
  articleId: string
  slug: string
  category: string
  sourceName: string | null
  personaTags: string[]
}

export function ArticleTracker({
  articleId,
  slug,
  category,
  sourceName,
  personaTags,
}: ArticleTrackerProps) {
  const searchParams = useSearchParams()

  useEffect(() => {
    // Track article view
    captureEvent('article_viewed', {
      articleId,
      slug,
      category,
      source: sourceName,
      persona_tags: personaTags,
    })

    // Track social referral clicks via UTM parameters
    const utmSource = searchParams.get('utm_source')
    const utmMedium = searchParams.get('utm_medium')
    const utmCampaign = searchParams.get('utm_campaign')

    if (utmSource) {
      captureEvent('social_referral_click', {
        articleId,
        slug,
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId])

  return null
}

interface ShareButtonProps {
  articleId: string
  slug: string
  title: string
}

export function ShareButton({ articleId, slug, title }: ShareButtonProps) {
  async function handleShare(method: 'twitter' | 'linkedin' | 'copy') {
    captureEvent('article_shared', { articleId, slug, shareMethod: method })

    const url = `${window.location.origin}/news/${slug}`

    if (method === 'twitter') {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer'
      )
    } else if (method === 'linkedin') {
      window.open(
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
        '_blank',
        'noopener,noreferrer'
      )
    } else if (method === 'copy') {
      try {
        await navigator.clipboard.writeText(url)
      } catch {
        // fallback: no-op
      }
    }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleShare('twitter')}
        className="flex items-center gap-1.5 hover:text-white transition-colors text-white/70"
        aria-label="Share on X / Twitter"
      >
        𝕏
      </button>
      <button
        onClick={() => handleShare('linkedin')}
        className="flex items-center gap-1.5 hover:text-white transition-colors text-white/70"
        aria-label="Share on LinkedIn"
      >
        in
      </button>
      <button
        onClick={() => handleShare('copy')}
        className="flex items-center gap-1.5 hover:text-white transition-colors text-white/70"
        aria-label="Copy link"
      >
        🔗
      </button>
    </div>
  )
}
